import { RootState, ThunkDispatch } from "core-integration/src/store";
import {
  getElementTypes,
  getElementTypesById,
} from "core-integration/src/store/element-types/reducers";
import { getElementControls } from "core-integration/src/store/elements/reducers";
import {
  showElementSummary,
  updateElementControlsAndMaxCost,
  updateElementTypeControl,
} from "core-integration/src/store/elements/thunks";
import {
  IElement,
  IElementControls,
} from "core-integration/src/store/elements/types";
import {
  getAffordableElementsFromControls,
  getProposedElementsById,
  getToSpend,
  getTypesNeeded,
} from "core-integration/src/store/squad/reducers";
import {
  proposeElement,
  removeElement,
} from "core-integration/src/store/squad/thunks";
import { getTeamsById } from "core-integration/src/store/teams/reducers";
import { integerToMoney } from "core-integration/src/utils/money";
import groupBy from "lodash/groupBy";
import React, { useEffect, useState } from "react";
import { Trans, withTranslation, WithTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Box } from "rebass/styled-components";
import styled from "styled-components";
import { createInteractionData } from "../../utils/tealium";
import Alert from "../Alert";
import BoldLinkButton from "../BoldLinkButton";
import Button from "../Button";
import Dialog, { DialogButtonItem } from "../Dialog";
import Paginator from "../element-controls/Paginator";
import ElementListRow from "../ElementListRow";
import { ElementTable } from "../ElementTable";
import { ControlArrowStart } from "../icons/Arrows";
import Tooltip, { TooltipLabel } from "../Tooltip";
import { BoxWrap, PrimaryGradient } from "../Utils";
import { ElementSearchField } from "./ElementSearchField";
import { defaultElementFilters, FilterChipRow } from "./SquadSelectionFilters";

const SectionHeader = styled.div`
  ${PrimaryGradient}
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top-left-radius: ${(props) => props.theme.radii[1]};
  border-top-right-radius: ${(props) => props.theme.radii[1]};
`;

const SectionHeading = styled.h3`
  margin: 1.5rem 1rem;
  color: ${(props) => props.theme.colors.offBlack};
  font-family: ${(props) => props.theme.fonts.body};
  font-size: ${(props) => props.theme.fontSizes[2]};
  font-weight: 500;
  line-height: 1;
`;

const ElementAddRemove = styled.th`
  width: 10%;
  @media (min-width: ${({ theme }) => theme.breakpoints[3]}) {
    display: none;
  }
`;

const ElementsBudgetWrap = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 ${({ theme }) => theme.space[3]};
`;

const BudgetContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${({ theme }) => theme.space[2]};
  margin: ${({ theme }) => theme.space[2]} 0;

  @media (min-width: ${({ theme }) => theme.breakpoints[3]}) {
    display: none;
  }
`;

const BudgetTitle = styled.span`
  color: ${({ theme }) => theme.colors.offBlack};
  text-transform: capitalize;
`;

const BudgetValue = styled.span<{ $value: number }>`
  color: ${({ $value, theme }) =>
    $value < 0 ? theme.colors.red : theme.colors.green};
  font-weight: bold;
`;

const ElementsShown = styled.p`
  margin: ${({ theme }) => theme.space[2]} 0;
  text-align: center;

  strong {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const ElementListElement = styled.th`
  width: 46%;

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    width: 50%;
  }
`;

const ElementListPrice = styled.th`
  width: 22%;

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    width: 18%;
  }
`;

const ElementListStat = styled.th`
  width: 15%;

  @media (min-width: ${({ theme }) => theme.breakpoints[3]}) {
    width: 22%;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    width: 19%;
  }
`;

const ElementTypeButton = styled.span`
  padding: 0;
  border: 0;
  background-color: transparent;
  color: currentColor;
  font-family: ${(props) => props.theme.fonts.body};
  font-size: ${(props) => props.theme.fontSizes[2]};
  font-weight: 500;
  text-transform: capitalize;
  cursor: pointer;
`;

const ButtonWrap = styled.div`
  margin-left: ${(props) => props.theme.space[2]};
  margin-right: ${(props) => props.theme.space[2]};
  margin-bottom: ${(props) => props.theme.space[4]};

  @media (min-width: ${({ theme }) => theme.breakpoints[1]}) {
    width: 33%;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints[3]}) {
    display: none;
  }
`;

const PlayerActions = styled.div`
  @media (min-width: ${({ theme }) => theme.breakpoints[3]}) {
    width: 50%;
    margin: 0 auto;
  }
`;

const SearchFieldWraper = styled.div`
  margin: ${({ theme }) => theme.space[2]} 0;
  padding: 0 ${({ theme }) => theme.space[2]};
`;

interface IOwnProps {
  hideListRef: React.RefObject<HTMLButtonElement>;
  hideSidebar: () => void;
  contentTitle: string;
}

type Props = IOwnProps & WithTranslation;

const ElementList = ({ contentTitle, hideListRef, hideSidebar, t }: Props) => {
  const dispatch = useDispatch<ThunkDispatch>();

  const [elementForMenu, setElementForMenu] = useState<IElement | null>(null);
  const [page, setPage] = useState<number>(1);

  const controls = useSelector(getElementControls);
  const currencyDivisor = 10;
  const elementTypes = useSelector((state: RootState) =>
    [...getElementTypes(state)].reverse(),
  );
  const elementTypesById = useSelector(getElementTypesById);
  const elements = useSelector(getAffordableElementsFromControls);
  const proposedElementsById = useSelector(getProposedElementsById);
  const teamsById = useSelector(getTeamsById);
  const typesNeeded = useSelector(getTypesNeeded);
  const toSpend = useSelector(getToSpend);

  const showElementDialog = (elementId: number) =>
    dispatch(showElementSummary(elementId));
  const updateControlsAndMaxCost = (controls: IElementControls) =>
    dispatch(updateElementControlsAndMaxCost(controls));

  const showElementType = (elementTypeId: number) =>
    dispatch(updateElementTypeControl(elementTypeId));

  const addElement = (element: IElement) => {
    createInteractionData({
      contentTitle: contentTitle,
      interactionId: "nba-fantasy-game:add-player:button",
      interactionText: `${element.first_name} ${element.second_name} ${t(
        "elementList.addPlayer",
        "Add player",
      )},`,
    });
    dispatch(proposeElement(element.id));
    hideSidebar();
    handleHideMenuForElement();
  };

  const handleRemoveElement = (position: number) => {
    dispatch(removeElement(position));
    handleHideMenuForElement();
  };

  const handleShowMenuForElement = (element: IElement) =>
    setElementForMenu(element);

  const handleHideMenuForElement = () => {
    setElementForMenu(null);
  };

  const showDialog = (element: IElement) => {
    createInteractionData({
      contentTitle: contentTitle,
      interactionId: "nba-fantasy-game:view-information:button",
      interactionText: `${element.first_name} ${element.second_name} ${t(
        "viewInformation",
        "View information",
      )}`,
    });
    showElementDialog(element.id);
    handleHideMenuForElement();
  };

  useEffect(() => {
    updateControlsAndMaxCost({
      ...controls,
      ...defaultElementFilters,
      search: "",
      getUnavailable: false,
    });
  }, []);

  const paginateAndGroup = (elements: IElement[], pageSize: number) => {
    const start = (page - 1) * pageSize;
    return {
      data: groupBy(elements.slice(start, start + pageSize), "element_type"),
      totalPages: Math.ceil(elements.length / pageSize),
    };
  };

  const renderMenu = () => {
    const element = elementForMenu;

    if (!element) {
      return null;
    }

    const proposedElement = proposedElementsById[element.id];
    const roomInSquad = typesNeeded[element.element_type];

    let alert = null;
    if (!roomInSquad) {
      alert = (
        <Alert type="error" noRadius>
          {t(
            "elementList.errors.maxElementType",
            "You already have the maximum number of {{ position }}",
            {
              position: elementTypesById[element.element_type].plural_name,
            },
          )}
        </Alert>
      );
    } else if (proposedElement) {
      alert = (
        <Alert type="warning" noRadius>
          <Trans
            i18nKey="elementList.errors.alreadySelectedn"
            values={{ playerName: element.web_name }}
            defaults={"<0>{{playerName}}</0> is already on your roster."}
            components={[<strong />]}
          />
        </Alert>
      );
    }

    return (
      <Dialog closeDialog={handleHideMenuForElement}>
        <Dialog.Header closeDialog={handleHideMenuForElement}>
          {`${element.first_name} ${element.second_name}`}
        </Dialog.Header>
        {alert && alert}

        <Dialog.Body>
          <PlayerActions>
            <DialogButtonItem>
              {proposedElement ? (
                <Button
                  onClick={() => handleRemoveElement(proposedElement.position)}
                  fullwidth="true"
                >
                  {t("elementList.removePlayer", "Remove player")}
                </Button>
              ) : (
                <Button
                  onClick={() => {
                    addElement(element);
                  }}
                  fullwidth="true"
                  disabled={Boolean(!roomInSquad)}
                >
                  {t("elementList.addPlayer", "Add player")}
                </Button>
              )}
            </DialogButtonItem>
            <DialogButtonItem>
              <Button
                onClick={() => showDialog(element)}
                fullwidth="true"
                variant="secondary"
              >
                {t("viewInformation", "View information")}
              </Button>
            </DialogButtonItem>
          </PlayerActions>
        </Dialog.Body>
      </Dialog>
    );
  };

  const { data, totalPages } = paginateAndGroup(elements.data, 30);

  return (
    <Box mb={4}>
      <ButtonWrap>
        <BoldLinkButton
          icon={<ControlArrowStart />}
          isBack={true}
          label={t("back", "Back")}
          onClick={hideSidebar}
          ref={hideListRef}
          variant="secondary"
        />
      </ButtonWrap>
      <BoxWrap>
        <SectionHeader>
          <SectionHeading>
            {t("elementList.playerSelection", "Player Selection")}
          </SectionHeading>
        </SectionHeader>
        <SearchFieldWraper>
          <ElementSearchField />
        </SearchFieldWraper>
        <FilterChipRow />
        <ElementsShown>
          <Trans
            i18nKey="elementList.playersShown"
            values={{ length: elements.data.length }}
            defaults={"<0>{{length}}</0> players shown"}
            components={[<strong />]}
          />
        </ElementsShown>
        <ElementsBudgetWrap>
          <ElementsShown>
            <Trans
              i18nKey="elementList.playersShown"
              values={{ length: elements.data.length }}
              defaults={"<0>{{length}}</0> players shown"}
              components={[<strong />]}
            />
          </ElementsShown>
          <BudgetContainer>
            <BudgetTitle>{t("elementList.budget", "Budget")}:</BudgetTitle>
            <BudgetValue $value={toSpend}>
              {integerToMoney(toSpend, currencyDivisor)}
            </BudgetValue>
          </BudgetContainer>
        </ElementsBudgetWrap>
        {elementTypes.map(
          (et) =>
            data[et.id] && (
              <Box mb={3} key={et.id}>
                <ElementTable elementTypeId={et.id}>
                  <thead>
                    <tr>
                      <th>&nbsp;</th>
                      <ElementListElement>
                        <ElementTypeButton
                          onClick={() => showElementType(et.id)}
                        >
                          {et.plural_name}
                        </ElementTypeButton>
                      </ElementListElement>
                      <ElementListPrice>
                        <Tooltip content={t("elementList.price", "Salary")}>
                          <TooltipLabel>$</TooltipLabel>
                        </Tooltip>
                      </ElementListPrice>
                      <ElementListStat>**</ElementListStat>
                      <ElementAddRemove>&nbsp;</ElementAddRemove>
                    </tr>
                  </thead>
                  <tbody>
                    {data[et.id].map((e) => (
                      <ElementListRow
                        key={e.id}
                        element={e}
                        hasRoomInPosition={Boolean(typesNeeded[e.element_type])}
                        proposedPosition={proposedElementsById[e.id]?.position}
                        isProposed={proposedElementsById[e.id] ? true : false}
                        handleRemoveElement={(elementPosition) =>
                          handleRemoveElement(elementPosition)
                        }
                        handleAddElement={() => addElement(e)}
                        renderElementMenu={() => handleShowMenuForElement(e)}
                        renderElementDialog={() => showDialog(e)}
                        team={teamsById[e.team]}
                        currencyDivisor={currencyDivisor}
                        sort={controls.sort}
                      />
                    ))}
                  </tbody>
                </ElementTable>
              </Box>
            ),
        )}
        {renderMenu()}
        <Paginator totalPages={totalPages} page={page} setPage={setPage} />
      </BoxWrap>
    </Box>
  );
};

export default withTranslation()(ElementList);
